'use client';
import React, { useEffect, useState } from 'react';
import Typography from '@/components/primitives/typography';

import styles from './styles.module.scss';
import ErrorText from '@/components/common/error-text';
import { Skeleton } from '@/components/primitives/skeleton';

export type ImageVariant = '' |
  'original' |
  'w96' |
  'w128' |
  'w256' |
  'w512' |
  'w1024' |
  'w2048' |
  'w4096';

// CDN(image-resizer)이 실제로 생성해 제공하는 너비 변형만 나열한다.
const VARIANT_WIDTHS: Partial<Record<ImageVariant, number>> = {
  w96: 96,
  w128: 128,
  w256: 256,
  w512: 512,
  w1024: 1024,
  w2048: 2048,
  w4096: 4096,
};

const VARIANT_ORDER: ImageVariant[] = ['w96', 'w128', 'w256', 'w512', 'w1024', 'w2048', 'w4096'];

// baseSrc 기준으로, upTo 변형의 너비 이하인 변형들만 모아 srcSet을 만든다.
// upTo가 지원 너비 목록에 없으면(예: original) srcSet을 만들지 않고 단일 src로 폴백한다.
const buildSrcSet = (baseSrc: string, upTo: ImageVariant): string | undefined => {
  const maxWidth = VARIANT_WIDTHS[upTo];
  if (!maxWidth) return undefined;

  return VARIANT_ORDER
    .filter((variant) => (VARIANT_WIDTHS[variant] ?? 0) <= maxWidth)
    .map((variant) => `${baseSrc}/${variant} ${VARIANT_WIDTHS[variant]}w`)
    .join(', ');
};

// Define the props for the LNImage component
type LNImageProps = {
  source: string;
  width?: number;
  height?: number;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  variant?: ImageVariant;
  sizes?: string;
  onError?: () => void;
  contentFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /** true면 LCP 후보 이미지로 취급해 즉시(관찰 없이) 로드하고 fetchPriority="high"를 준다. */
  priority?: boolean;
  // Add any other props needed
};

/**
 * LNImage component
 *
 * Features:
 * - Supports variants (default: original)
 * - Displays error message when image loading fails
 * - Loads images lazily by default
 * - Supports contentFit property to control how the image fits within its container
 */
const LNImage = ({
                   source,
                   width,
                   height,
                   alt = '',
                   className = '',
                   style = {},
                   variant = 'original',
                   sizes,
                   contentFit,
                   onError,
                   priority = false,
                   ...props
                 }: LNImageProps) => {
  // State to track if image loading failed
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentVariant, setCurrentVariant] = useState(variant);
  const [src, setSrc] = useState('');
  // priority 이미지는 IntersectionObserver를 기다리지 않고 바로 로드한다.
  const [isInView, setIsInView] = useState(priority);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Handle image load error
  const handleError = () => {
    // variant가 original이 아닌 경우 original로 폴백 시도
    if(variant == 'w256') console.log(`${source.substring(0, 100)} = ${variant}`);

    if (currentVariant !== 'original') {
      if(variant == 'w256') console.log(`${source.substring(0, 100)} = ${variant} setOrigin`);
      setCurrentVariant('original');
      return;
    }

    if(variant == 'w256') console.log(`${source.substring(0, 100)} = ${variant} second error`);

    setLoadError(true);
    // Call the onError callback if provided
    if (onError) {
      onError();
    }
  };

  useEffect(() => {
    setLoadError(false);
    setIsLoading(true);
    setCurrentVariant(variant);
    setSrc(source);
    setIsInView(priority); // source가 바뀌면 다시 관찰해야 함 (priority는 즉시 표시)
  }, [source, variant, priority]);

  useEffect(() => {
    if (priority) return; // priority 이미지는 관찰 없이 이미 로드 대상이다

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { rootMargin: '200px' } // 미리 로딩하기 위해 마진 추가
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [source, priority]); // source가 바뀌면 새로 관찰할 수도 있음

  const imageSrc = isInView ? [src, currentVariant].filter(a => !!a).join('/') : undefined;
  const imageSrcSet = isInView && src ? buildSrcSet(src, currentVariant) : undefined;

  return (
    <div ref={containerRef} style={{ position: 'relative', width, height, ...style }} className={className}>
      {loadError ? (
        <div className={styles.errorContainer} style={{ width, height }}>
          <ErrorText>
            <Typography className={styles.errorText}>
              이미지 다운로드를 실패했습니다.
            </Typography>
          </ErrorText>
        </div>
      ) : (
        <>
          {imageSrc && (
            <img
              src={imageSrc}
              srcSet={imageSrcSet}
              sizes={imageSrcSet ? (sizes ?? '(max-width: 720px) 100vw, 720px') : undefined}
              width={width}
              height={height}
              alt={alt}
              className={`${styles.lnImage} ${className}`}
              style={{
                width,
                height,
                objectFit: contentFit,
                display: isLoading ? 'none' : undefined,
                ...style
              }}
              loading={priority ? 'eager' : undefined}
              fetchPriority={priority ? 'high' : undefined}
              onLoad={() => setIsLoading(false)}
              onError={handleError}
              {...props}
            />
          )}
          {isLoading && (
            <Skeleton
              className={className}
              style={{ width, height, position: 'absolute', top: 0, left: 0, ...style }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default LNImage;
